/**
 * 教材笔记（手写标注）的云端读写。
 *
 * 一份笔记 = 一个用户 × 一份教材。后端按 Cognito 的 sub 隔离，
 * 请求体里不带任何用户标识，因此拿不到别人的笔记。
 *
 * 笔迹本身是**矢量笔画**（坐标按页面宽高归一化到 0..1），所以：
 * 换设备、换缩放、横竖屏切换都不会错位或糊。
 *
 * **保留策略**：笔记默认不保存，老师主动点 Save notes 才会调到这里；
 * 存下来的保留 7 天（见 RETENTION_DAYS），之后由 DynamoDB TTL 和 S3 生命周期自动清理。
 */

const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// 笔记数据结构版本，必须与 lambda/portal/materialAnnotationFn.py 中的 NOTES_VERSION 一致
export const NOTES_VERSION = 1;

// 主动保存后的保留天数，必须与后端 RETENTION_DAYS 和 S3 生命周期规则一致
export const RETENTION_DAYS = 7;

const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

const annotationApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/material-annotation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ operation, data })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * 取回当前用户在某份教材上的笔记。超过保留期的笔记后端当作不存在。
 *
 * @returns {Object} { found, notes?, updatedAt?, strokeCount?, pageCount?, expiresAt? }
 */
export const getMaterialNotes = async (materialId) => annotationApiRequest('get', { materialId });

/**
 * 覆盖式保存笔记（老师主动点 Save notes 时才调用，不做增量合并）。
 * 每次保存都会把 7 天保留期重新计时。
 *
 * @param {string} materialId
 * @param {Object} pages - { [页码]: 笔画数组 }
 */
export const saveMaterialNotes = async (materialId, pages) =>
  annotationApiRequest('save', { materialId, notes: { version: NOTES_VERSION, pages } });

/** 删除当前用户在某份教材上的笔记（笔记被清空时调用）。 */
export const deleteMaterialNotes = async (materialId) => annotationApiRequest('delete', { materialId });

/** 列出当前用户所有存过笔记的教材（给「有笔记」角标用）。 */
export const listMaterialNotes = async () => {
  const result = await annotationApiRequest('list', {});
  return result.annotations || [];
};
